import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { MatIconModule } from '@angular/material/icon';
import { ToastrService } from 'ngx-toastr';
import { Subscription, finalize } from 'rxjs';
import { CargoheatingCommonService } from 'src/app/shared/service/cargo-heating-service/cargoheating-common.service';
import { FloatingUploadPreviewService } from 'src/app/shared/service/floating-upload-preview/floating-upload-preview.service';
import { isUploadFilePreviewable } from 'src/app/shared/helpers/upload-file-preview.util';

const EXTENSION_BY_CONTENT_TYPE: Record<string, string> = {
  'application/pdf': 'pdf',
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'application/json': 'json',
  'text/csv': 'csv',
  'application/vnd.ms-excel': 'xls',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
  'application/msword': 'doc',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
};

/**
 * Compact drag & drop / browse strip that stores one supporting document per
 * onboarding section (imo + doc_type) and lets the user open it again.
 *
 * Usage:
 *   <app-ch-section-document [imo]="imo" docType="capacity_plan" label="Capacity plan">
 *   </app-ch-section-document>
 *
 * Pass `[readonly]="true"` where the document should only be viewable (no upload/replace).
 */
@Component({
  selector: 'app-ch-section-document',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './ch-section-document.component.html',
  styleUrls: ['./ch-section-document.component.scss'],
})
export class ChSectionDocumentComponent implements OnChanges, OnDestroy {
  @Input() imo: string | number | null = '';
  @Input() docType = '';
  @Input() label = 'Document';
  @Input() accept = '.pdf,.png,.jpg,.jpeg,.webp,.xlsx,.xls,.csv,.doc,.docx';
  @Input() readonly = false;

  hasDocument = false;
  storedFileName: string | null = null;
  isChecking = false;
  isUploading = false;
  isOpening = false;
  isDragOver = false;

  private checkSub: Subscription | null = null;

  constructor(
    private cargoHeatingService: CargoheatingCommonService,
    private floatingPreview: FloatingUploadPreviewService,
    private toastr: ToastrService,
  ) {}

  get resolvedImo(): string {
    return String(this.imo ?? '').replace(/\D/g, '');
  }

  get isReady(): boolean {
    return this.resolvedImo.length === 7 && !!this.docType;
  }

  get isBusy(): boolean {
    return this.isUploading || this.isOpening;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['imo'] || changes['docType']) {
      this.refreshStatus();
    }
  }

  ngOnDestroy(): void {
    this.checkSub?.unsubscribe();
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (this.isReady && !this.isBusy && !this.readonly) {
      this.isDragOver = true;
    }
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (!this.isReady || this.isBusy || this.readonly) {
      return;
    }
    const files = event.dataTransfer?.files;
    if (files && files.length > 1) {
      this.toastr.info('Only one document is kept per section — uploading the first file.', this.label);
    }
    this.upload(files?.[0] ?? null);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.upload(input.files?.[0] ?? null);
    input.value = '';
  }

  openDocument(): void {
    if (!this.isReady || this.isBusy) {
      return;
    }
    this.isOpening = true;
    this.cargoHeatingService
      .getOnboardingDocument(this.resolvedImo, this.docType)
      .pipe(finalize(() => (this.isOpening = false)))
      .subscribe({
        next: (response) => this.showDocument(response),
        error: (err: HttpErrorResponse) => {
          if (err.status === 404) {
            this.hasDocument = false;
            this.storedFileName = null;
            this.toastr.info(`No ${this.label.toLowerCase()} uploaded yet.`, this.label);
            return;
          }
          this.toastr.error('Could not fetch the document. Please try again.', this.label);
        },
      });
  }

  private upload(file: File | null): void {
    if (!file || !this.isReady || this.readonly) {
      return;
    }
    if (!/\.[^./\\]+$/.test(file.name)) {
      this.toastr.warning('The file must have an extension (e.g. .pdf).', this.label);
      return;
    }

    this.checkSub?.unsubscribe();
    this.isUploading = true;
    this.cargoHeatingService
      .uploadOnboardingDocument(this.resolvedImo, this.docType, file)
      .pipe(finalize(() => (this.isUploading = false)))
      .subscribe({
        next: () => {
          this.hasDocument = true;
          this.storedFileName = file.name;
          this.toastr.success(`${file.name} uploaded.`, this.label);
        },
        error: (err: HttpErrorResponse) => {
          const message = err?.error?.message || 'Upload failed. Please try again.';
          this.toastr.error(message, this.label);
        },
      });
  }

  private refreshStatus(): void {
    this.checkSub?.unsubscribe();
    this.hasDocument = false;
    this.storedFileName = null;
    this.isChecking = false;
    if (!this.isReady) {
      return;
    }

    this.isChecking = true;
    this.checkSub = this.cargoHeatingService
      .checkOnboardingDocument(this.resolvedImo, this.docType)
      .pipe(finalize(() => (this.isChecking = false)))
      .subscribe({
        next: (response) => {
          this.hasDocument = true;
          this.storedFileName = this.fileNameFromHeaders(response);
        },
        // 404 simply means nothing has been uploaded for this section yet.
        error: () => {
          this.hasDocument = false;
        },
      });
  }

  private showDocument(response: HttpResponse<Blob>): void {
    const blob = response.body;
    if (!blob) {
      this.toastr.error('The document is empty.', this.label);
      return;
    }

    const fileName = this.fileNameFromHeaders(response, blob.type) || this.docType;
    this.hasDocument = true;
    this.storedFileName = fileName;

    const objectUrl = URL.createObjectURL(blob);
    if (isUploadFilePreviewable(fileName)) {
      this.floatingPreview.open({
        fileName,
        previewUrl: objectUrl,
        onClose: () => URL.revokeObjectURL(objectUrl),
      });
      return;
    }

    // Excel / Word / CSV cannot be previewed in the browser — hand it over as a download.
    const anchor = document.createElement('a');
    anchor.href = objectUrl;
    anchor.download = fileName;
    anchor.click();
    setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
  }

  /**
   * Reads the stored file name from Content-Disposition (needs the backend to expose
   * that header over CORS); otherwise builds one from doc_type + Content-Type.
   */
  private fileNameFromHeaders(response: HttpResponse<unknown>, blobType = ''): string | null {
    const disposition = response.headers.get('Content-Disposition') || '';
    const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
    if (match?.[1]) {
      try {
        return decodeURIComponent(match[1].trim());
      } catch {
        return match[1].trim();
      }
    }

    const contentType = (response.headers.get('Content-Type') || blobType).split(';')[0].trim();
    const extension = EXTENSION_BY_CONTENT_TYPE[contentType.toLowerCase()];
    return extension ? `${this.docType}.${extension}` : null;
  }
}
